/**
 * Centralized Modal Stack Manager
 * Handles FILO (First In, Last Out) modal closing with escape key
 */
class ModalStack {
  constructor() {
    this.stack = []
    this.listeners = new Set()
    this.isListening = false
  }

  // Add modal to stack
  push(modalId, closeCallback, closeOnEscape = false) {
    // Check if already exists
    const existingIndex = this.stack.findIndex(modal => modal.id === modalId)
    if (existingIndex !== -1) {
      // Update existing modal instead of adding duplicate
      this.stack[existingIndex].closeCallback = closeCallback
      this.stack[existingIndex].closeOnEscape = closeOnEscape
      this.stack[existingIndex].timestamp = Date.now()
      
      // Notify listeners of update
      this._notifyListeners('update', this.stack[existingIndex])
      return
    }
    
    const modal = {
      id: modalId,
      closeCallback,
      closeOnEscape,
      timestamp: Date.now()
    }
    
    this.stack.push(modal)
    this._setupListener()
    
    // Notify listeners of stack change
    this._notifyListeners('push', modal)
  }

  // Remove modal from stack
  remove(modalId) {
    const index = this.stack.findIndex(modal => modal.id === modalId)
    if (index !== -1) {
      const removedModal = this.stack.splice(index, 1)[0]
      
      // Clean up listener if stack is empty
      if (this.stack.length === 0) {
        this._cleanupListener()
      }
      
      // Notify listeners of stack change
      this._notifyListeners('remove', removedModal)
      
      return removedModal
    }
    return null
  }

  // Get the top modal (most recently opened)
  getTop() {
    return this.stack.length > 0 ? this.stack[this.stack.length - 1] : null
  }

  // Get all modals in stack
  getStack() {
    return [...this.stack]
  }

  // Handle escape key press
  _handleEscape = (event) => {
    if (event.key === 'Escape') {
      // Find the topmost modal that allows escape closing
      for (let i = this.stack.length - 1; i >= 0; i--) {
        const modal = this.stack[i]
        if (modal.closeOnEscape) {
          event.preventDefault()
          event.stopPropagation()
          
          // Call the close callback
          modal.closeCallback()
          break
        }
      }
    }
  }

  // Set up the global escape key listener (only once)
  _setupListener() {
    if (!this.isListening) {
      window.addEventListener('keydown', this._handleEscape, true) // Use capture phase
      this.isListening = true
    }
  }

  // Clean up the global escape key listener
  _cleanupListener() {
    if (this.isListening) {
      window.removeEventListener('keydown', this._handleEscape, true)
      this.isListening = false
    }
  }

  // Subscribe to stack changes
  subscribe(callback) {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }

  // Notify all listeners of stack changes
  _notifyListeners(action, modal) {
    this.listeners.forEach(callback => {
      try {
        callback({ action, modal, stack: this.getStack() })
      } catch (error) {
        console.error('ModalStack listener error:', error)
      }
    })
  }

  // Debug method to log current stack
  debug() {
    const stackInfo = this.stack.map(m => ({
      id: m.id,
      closeOnEscape: m.closeOnEscape,
      timestamp: new Date(m.timestamp).toISOString()
    }))
    
    console.log('Modal Stack:', stackInfo)
    console.log(`Total modals: ${this.stack.length}, Escapable: ${this.stack.filter(m => m.closeOnEscape).length}`)
    
    if (this.stack.length > 0) {
      const top = this.getTop()
      console.log(`Top modal: ${top.id} (closeOnEscape: ${top.closeOnEscape})`)
    }
    
    return stackInfo
  }

  // Get count of escapable modals
  getEscapableCount() {
    return this.stack.filter(m => m.closeOnEscape).length
  }

  // Clear all modals (useful for debugging)
  clear() {
    const count = this.stack.length
    this.stack = []
    this._cleanupListener()
    console.log(`Cleared ${count} modals from stack`)
    return count
  }

  // Clean up all modals and listeners
  destroy() {
    this.stack = []
    this.listeners.clear()
    this._cleanupListener()
  }
}

// Create singleton instance
const modalStack = new ModalStack()

// Export singleton instance
export default modalStack

// Also export the class for testing purposes
export { ModalStack }